import isEmpty from 'lodash/isEmpty';
import React from 'react';
import { useSelector } from 'react-redux';
import { getPathName } from 'store/router/routerSelectors/routerGetters';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { HOTELS_HOST, PUBLIC_PATH } from 'config';
import { DESTINATION_STATUS } from 'lib/enums/destination';

const canonicalLink = (pathname) => `${HOTELS_HOST}/hotels${pathname}`;

const getMeta = ({ destination, pathname }) => {
  const meta = [
    { name: 'hotels-booking-stage', content: 'destination-guide' },
    { name: 'og:site_name', content: 'Qantas Hotels' },
  ];

  const hasResolved = pathname && destination.status === DESTINATION_STATUS.RESOLVED;
  const heroImage = destination?.heroImage?.assets?.large;

  if (hasResolved) {
    meta.push({
      name: 'description',
      content: `Compare and book hotels in ${destination.regionName}. Choose from over 200,000 accommodation options + earn 3 Qantas Points per $1 spent with Qantas Hotels`,
    });
    meta.push({ name: 'robots', content: 'index, follow' });
    meta.push({ name: 'og:title', content: getPageTitle(destination) });
    meta.push({ name: 'og:url', content: canonicalLink(pathname) });

    heroImage && meta.push({ name: 'og:image', content: `${PUBLIC_PATH}${destination.heroImage.assets.large}` });
  } else {
    meta.push({
      name: 'description',
      content: `Compare and book hotels with Qantas Hotels. Choose from over 200,000 accommodation options + earn 3 Qantas Points per $1 spent with Qantas Hotels`,
    });
    meta.push({ name: 'robots', content: 'noindex, nofollow' });
  }

  return meta;
};

const getPageTitle = (destination = {}) => {
  const { regionName, template } = destination;
  const isCity = regionName && template === 'city';
  const isCountryOrState = regionName && ['country', 'state'].includes(template);

  if (isCity) {
    return `${regionName} Accommodation - Find ${regionName} Hotel Deals | Qantas Hotels`;
  }

  if (isCountryOrState) {
    return `${regionName} Hotels & Accommodation | Qantas Hotels`;
  }

  return 'Hotels & Accommodation | Qantas Hotels';
};

const getHtmlAttributes = (destination) => {
  if (isEmpty(destination?.faqs)) {
    return {};
  }

  return {
    itemscope: true,
    itemtype: 'https://schema.org/FAQPage',
  };
};

const DestinationHelmet = (props) => {
  const pathname = useSelector(getPathName);
  return (
    <Helmet
      htmlAttributes={getHtmlAttributes(props.destination)}
      title={getPageTitle(props.destination)}
      meta={getMeta({ ...props, pathname })}
      link={[{ rel: 'canonical', href: canonicalLink(pathname) }]}
    />
  );
};

DestinationHelmet.propTypes = {
  destination: PropTypes.shape({
    heroImage: PropTypes.shape({
      assets: PropTypes.shape({
        large: PropTypes.string,
      }),
    }),
    regionName: PropTypes.string,
    status: PropTypes.string.isRequired,
  }).isRequired,
};

export default DestinationHelmet;
